import { CustomWidgetCollection, TextMarkdownEvent, PanelModel } from '@psyomics/surveyjs-core';
import { useMemo } from 'react';
import { ReactElementFactory, ReactQuestionFactory, SurveyModel } from 'survey-react-ui';

import { convertMarkdown } from '../../utils/convert-markdown';

import { psyomicsCustomTheme } from './base';
import { datePickerWidget } from './sub-components/surveyjs-component-overrides/date-picker-widget';
import { registerTextInputOverride } from './sub-components/surveyjs-component-overrides/question-text';
import { registerButtonOverride } from './sub-components/surveyjs-component-overrides/sv-nav-btn';
import { registerTocPsyomics } from './sub-components/surveyjs-component-overrides/toc-psyomics/toc-psyomics';
import { isSurveyQuestionFocused } from './utils/survey-info';

const onTextMarkdown = (_: SurveyModel, options: TextMarkdownEvent) => {
  options.html = convertMarkdown(options.text, true);
};

function novalidateAttrOnForms(_: unknown, options: { htmlElement: HTMLElement }) {
  options.htmlElement.querySelectorAll('form').forEach((el) => {
    el.setAttribute('novalidate', 'true');
  });
}

function goToTopOfPage() {
  window.scrollTo(0, 0);
}

function validationFailHandler(survey: SurveyModel, options: { panel: PanelModel }) {
  if (isSurveyQuestionFocused(survey)) return;
  options.panel.focusFirstErrorQuestion();
}

const extendSurveyModel = (model: SurveyModel) => {
  model.applyTheme(psyomicsCustomTheme);
  model.onTextMarkdown.add(onTextMarkdown);
  model.addLayoutElement({
    id: 'toc-psyomics',
    component: 'sv-toc-psyomics',
    container: 'header',
    data: model,
  });
  model.addLayoutElement({
    id: 'toc-psyomics-desktop',
    component: 'sv-toc-psyomics-desktop',
    container: 'left',
    data: model,
  });
  model.onAfterRenderSurvey.add(novalidateAttrOnForms);
  model.enterKeyAction = 'loseFocus';
  model.onCurrentPageChanged.add(goToTopOfPage);

  model.onValidatePanel.add(validationFailHandler);

  return { cleanup: () => cleanup(model) };
};

const cleanup = (model: SurveyModel) => {
  model.onTextMarkdown.remove(onTextMarkdown);
  model.onAfterRenderSurvey.remove(novalidateAttrOnForms);
  model.onCurrentPageChanged.remove(goToTopOfPage);
  model.onValidatePanel.remove(validationFailHandler);
};

const useExtendSurveyModel = (model: SurveyModel) => {
  useMemo(() => {
    cleanup(model);
    extendSurveyModel(model);
  }, [model]);
};

const registerComponents = () => {
  ReactElementFactory.Instance.registerElement('sv-nav-btn', registerButtonOverride);

  ReactQuestionFactory.Instance.registerQuestion('text', registerTextInputOverride);

  ReactElementFactory.Instance.registerElement('sv-toc-psyomics', registerTocPsyomics);

  ReactElementFactory.Instance.registerElement('sv-toc-psyomics-desktop', registerTocPsyomics);

  if (!CustomWidgetCollection.Instance.getCustomWidgetByName(datePickerWidget.name)) {
    CustomWidgetCollection.Instance.addCustomWidget(datePickerWidget, 'property');
  }
};

export {
  extendSurveyModel,
  goToTopOfPage,
  novalidateAttrOnForms,
  onTextMarkdown,
  registerComponents,
  useExtendSurveyModel,
  validationFailHandler,
};
